import React from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../cpanel/PageHeader';

/**
 * AdminDashboard - Panel central de configuración del sistema.
 * Adaptado con el lenguaje visual del Cpanel (cards directas sobre el fondo).
 */
const AdminDashboard = () => {
    const navigate = useNavigate();

    const modules = [
        {
            title: "Gestión Comercial",
            description: "Configura canales, subcanales, vendedores y reglas de distribución de comisión.",
            category: "Canales - Subcanales",
            icon: (
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            ),
            path: "/admin/comisiones",
            chipBg: "bg-blue-50 text-blue-700 border-blue-100",
            hoverBorder: "hover:border-blue-300"
        },
        {
            title: "Compañías y Códigos",
            description: "Gestiona el listado de aseguradoras y códigos de producción (PAS/Org).",
            category: "Aseguradoras",
            icon: (
                <svg className="w-6 h-6 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 v5m-4 0h4" />
                </svg>
            ),
            path: "/admin/companias",
            chipBg: "bg-teal-50 text-teal-700 border-teal-100",
            hoverBorder: "hover:border-teal-300"
        },
        {
            title: "Riesgos y Productos",
            description: "Define los riesgos (Autos, Vida, etc.) y sus subriesgos específicos.",
            category: "Riesgos - Subriesgos",
            icon: (
                <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
            ),
            path: "/admin/riesgos",
            chipBg: "bg-indigo-50 text-indigo-700 border-indigo-100",
            hoverBorder: "hover:border-indigo-300"
        },
        {
            title: "Reglas de Renovación",
            description: "Define si la renovación es Manual o Automática según Compañía, Riesgo y Subriesgo.",
            category: "Políticas",
            icon: (
                <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
            ),
            path: "/admin/reglas-renovacion",
            chipBg: "bg-emerald-50 text-emerald-700 border-emerald-100",
            hoverBorder: "hover:border-emerald-300"
        },
        {
            title: "Acuerdos Comerciales",
            description: "Tabla maestra de comisiones pactadas con Aseguradoras por riesgo.",
            category: "Comisiones",
            icon: (
                <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
            ),
            path: "/admin/acuerdos",
            chipBg: "bg-amber-50 text-amber-700 border-amber-100",
            hoverBorder: "hover:border-amber-300"
        },
        {
            title: "Liquidación de Comisiones",
            description: "Importación masiva y gestión de pagos a la red comercial.",
            category: "Liquidación",
            icon: (
                <svg className="w-6 h-6 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
            ),
            path: "/admin/liquidaciones",
            chipBg: "bg-cyan-50 text-cyan-700 border-cyan-100",
            hoverBorder: "hover:border-cyan-300"
        },
        {
            title: "Gestión de Contadores",
            description: "Administra los estudios contables y sus contactos asociados.",
            category: "Contactos",
            icon: (
                <svg className="w-6 h-6 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
            ),
            path: "/admin/contadores",
            chipBg: "bg-violet-50 text-violet-700 border-violet-100",
            hoverBorder: "hover:border-violet-300"
        },
        {
            title: "Equipo y Accesos",
            description: "Gestiona vendedores, suscriptores y usuarios del sistema.",
            category: "Usuarios",
            icon: (
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
            ),
            path: "/admin/equipo",
            chipBg: "bg-purple-50 text-purple-700 border-purple-100",
            hoverBorder: "hover:border-purple-300"
        },
        {
            title: "Gestión de Finanzas",
            description: "Control y seguimiento de gastos administrativos.",
            category: "Finanzas",
            icon: (
                <svg className="w-6 h-6 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            ),
            path: "/admin/facturacion-gestion",
            chipBg: "bg-rose-50 text-rose-700 border-rose-100",
            hoverBorder: "hover:border-rose-300"
        }
    ];

    return (
        <div className="min-h-screen bg-[#f3f4f8] font-sans text-left pb-16 w-full antialiased">
            
            {/* HEADER SUPERIOR UNIFICADO */}
                <div className="min-h-screen bg-[#f3f4f8]">
                <PageHeader 
                    title="Panel de Administración"
                    subtitle="Centro de control y configuración estratégica del sistema"
                />

            {/* CONTENIDO PRINCIPAL DIRECTO EN EL FONDO */}
            <div className="w-full px-8 pt-14 max-w-[1600px] mx-auto space-y-6">
                
                {/* CABECERA DE SECCIÓN (SIN CAJA BLANCA) */}
                <div className="flex items-center justify-between border-b border-slate-200/80 pb-4">
                    <div>
                        <h2 className="text-2xl font-bold text-slate-900 tracking-tight">
                            Módulos de Ajustes y Configuración
                        </h2>
                        <p className="text-sm text-slate-400 font-medium mt-1">
                            Selecciona una sección para gestionar la estructura comercial, parámetros y permisos
                        </p>
                    </div>
                    <span className="bg-purple-50 text-purple-700 text-[10px] font-black px-3 py-1 rounded-full border border-purple-200 uppercase tracking-wider">
                        {modules.length} Módulos
                    </span>
                </div>

                {/* GRID DE CARDS DIRECTAMENTE SOBRE EL FONDO */}
                <main className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {modules.map((item, index) => (
                        <div 
                            key={index}
                            onClick={() => navigate(item.path)}
                            className={`group bg-white border border-slate-200/80 ${item.hoverBorder} rounded-2xl p-5 transition-all duration-200 flex flex-col justify-between h-full shadow-2xs hover:shadow-xl hover:-translate-y-1 cursor-pointer`}
                        >
                            <div>
                                <div className="flex items-center justify-between mb-4">
                                    <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-100 group-hover:bg-white transition-colors">
                                        {item.icon}
                                    </div>
                                    <span className={`text-[10px] font-black uppercase tracking-wider px-2.5 py-0.5 rounded-md border ${item.chipBg}`}>
                                        {item.category}
                                    </span>
                                </div>

                                <h4 className="text-lg font-bold text-slate-900 group-hover:text-blue-600 transition-colors tracking-tight">
                                    {item.title}
                                </h4>

                                <p className="text-slate-400 text-xs leading-relaxed font-medium mt-1.5">
                                    {item.description}
                                </p>
                            </div>

                            <div className="mt-5 pt-3 border-t border-slate-100 flex items-center justify-between text-xs font-bold text-slate-400 group-hover:text-blue-600 transition-colors">
                                <span className="text-[11px] uppercase tracking-wider font-extrabold">Configurar</span>
                                <span className="transform group-hover:translate-x-1.5 transition-transform text-sm">→</span>
                            </div>
                        </div>
                    ))}
                </main>

            </div>

            {/* FOOTER */}
            <footer className="mt-16 pt-6 border-t border-slate-200/80 text-center select-none">
                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">
                    &copy; {new Date().getFullYear()} IB BROKERS SYSTEM &bull; INTELLIGENT INSURANCE MANAGEMENT &bull; v2.5
                </p>
            </footer>
        </div>
        </div>
    );
};

export default AdminDashboard;